import { matchPath } from 'react-router-dom'
import type { PathMatch } from 'react-router-dom'
import UrlLocation from 'src/lib/url/UrlLocation'

interface MatchLocaleAwarePathOptions {
  exact?: boolean
  path?: string | Array<string>
  sensitive?: boolean
}

type LocaleAwarePathPattern = string | Array<string> | MatchLocaleAwarePathOptions

type LocaleAwarePathMatch<Params extends { [K in keyof Params]?: string | undefined }> =
  Omit<PathMatch<string>, 'params'> & {
    isExact: boolean
    params: Params
  }

/**
 * App-level wrapper around React Router's `matchPath` for matching regional
 * routes against the real browser/Redux pathname.
 *
 * Use this when matching paths that may include locale-shaped URL segments,
 * e.g. `/au/de-DE/hotels` or `/au/offer/de-DE/abc123`, against existing route
 * patterns such as `/:regionCode/hotels`. Use raw `matchPath` when the pathname
 * is already locale-stripped, or when the code intentionally needs exact raw URL
 * semantics.
 */
export function matchLocaleAwarePath<Params extends { [K in keyof Params]?: string | undefined } = Record<string, string | undefined>>(
  pathname: string,
  pattern: LocaleAwarePathPattern,
) {
  const pathWithoutLocale = new UrlLocation(pathname).withoutLocale().path
  const paths = normalisePaths(pattern)
  const end = typeof pattern === 'object' && !Array.isArray(pattern) ? !!pattern.exact : false
  const caseSensitive = typeof pattern === 'object' && !Array.isArray(pattern) ? !!pattern.sensitive : false

  for (const path of paths) {
    const matched = matchPath({ path, end, caseSensitive }, pathWithoutLocale)
    if (matched) {
      return {
        ...matched,
        isExact: matched.pathname === pathWithoutLocale,
      } as LocaleAwarePathMatch<Params>
    }
  }

  return null
}

function normalisePaths(pattern: LocaleAwarePathPattern): Array<string> {
  if (typeof pattern === 'string') return [pattern]
  if (Array.isArray(pattern)) return pattern
  if (Array.isArray(pattern.path)) return pattern.path
  if (pattern.path) return [pattern.path]
  return ['*']
}
